import React, { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";

/* Lightweight SparklesCore (Aceternity-style API, no tsparticles dependency) —
   twinkling drifting dots on a transparent canvas. */
export const SparklesCore = ({
  id,
  background = "transparent",
  minSize = 0.6,
  maxSize = 1.4,
  particleDensity = 120,
  className,
  particleColor = "#FFFFFF",
  speed = 1,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let raf = 0;
    let particles = [];
    let w = 0;
    let h = 0;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const parent = canvas.parentElement || canvas;
      const rect = parent.getBoundingClientRect();
      w = Math.max(1, rect.width);
      h = Math.max(1, rect.height);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(700, Math.max(20, Math.floor(((w * h) / 40000) * particleDensity)));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: minSize + Math.random() * (maxSize - minSize),
        p: Math.random() * Math.PI * 2,
        s: (0.5 + Math.random()) * 0.03 * speed,
        vx: (Math.random() - 0.5) * 0.08,
        vy: (Math.random() - 0.5) * 0.08,
      }));
    };

    const paint = (animateTwinkle) => {
      ctx.clearRect(0, 0, w, h);
      for (const pt of particles) {
        if (animateTwinkle) {
          pt.p += pt.s;
          pt.x += pt.vx;
          pt.y += pt.vy;
          if (pt.x < 0) pt.x = w;
          if (pt.x > w) pt.x = 0;
          if (pt.y < 0) pt.y = h;
          if (pt.y > h) pt.y = 0;
        }
        ctx.globalAlpha = 0.15 + 0.85 * (0.5 + 0.5 * Math.sin(pt.p));
        ctx.fillStyle = particleColor;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, pt.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const loop = () => {
      paint(true);
      raf = window.requestAnimationFrame(loop);
    };

    resize();
    if (prefersReduced) {
      paint(false);
    } else {
      loop();
    }
    const onResize = () => resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [minSize, maxSize, particleDensity, particleColor, speed]);

  return (
    <canvas
      ref={canvasRef}
      id={id}
      className={cn("pointer-events-none", className)}
      style={{ background }}
      aria-hidden="true"
    />
  );
};

export default SparklesCore;
